<template lang="html">
  <item-editor-dialog
    :id="selectedItemId"
    ref="editForm"
    v-model="selectedPanel"
    :show="showItem"
    :clone-id="cloneId"
    :title="pageTitle"
    :on-close="doClose"
    :context-menu="defaultContextMenu"
    :lock-timer="lockTimerSettings"
    :loading="isDataLoading"
    :errors="errors"
    :edit-panel-id="editPanel"
    :audit-panel-id="auditPanel"
    :audit="auditHistorySettings"
    :buttons="pageBarButtons"
  >
    <!-- START OF FORM FIELDS -->
    <expansion-card icon="mdi-silverware-fork-knife">
      <span slot="title">Meal Period</span>
      <v-flex class="pt-3" />
      <v-layout wrap>
        <v-flex class="xs12 sm6 md6 px-3">
          <v-text-field
            v-model.trim="itemData.name"
            :label="type + ' Name'"
            :rules="rules().required('Name')"
            :error-messages="getPathErrors(jsonPath, 'name')"
            :disabled="isFormDisabled"
            required
            @input="clearPathErrors(jsonPath, 'name')"
          />
        </v-flex>
        <v-flex class="xs12 sm6 md6 px-3">
          <v-autocomplete
            v-model="itemData.locationId"
            label="Location"
            placeholder="Select Location"
            :items="locations.filter((l) => { return l.id === itemData.locationId || !l.eventOnly })"
            :rules="rules().required()"
            required
            auto
            :error-messages="getPathErrors(jsonPath, 'locationId')"
            :disabled="isFormDisabled"
            item-text="name"
            item-value="id"
            @input="clearPathErrors(jsonPath, 'locationId')"
          />
        </v-flex>
        <v-flex class="xs12 sm12 md4 px-3">
          <v-autocomplete
            v-model="itemData.tableConfigId"
            label="Table Configuration"
            :placeholder="selectedLocationTableConfigs[0] ? 'Select Table Configuration' : 'No Applicable Table Configuration'"
            :items="selectedLocationTableConfigs"
            auto
            clearable
            :error-messages="getPathErrors(jsonPath, 'tableConfigId')"
            :disabled="isFormDisabled"
            item-text="name"
            item-value="id"
            @input="clearPathErrors(jsonPath, 'tableConfigId')"
          />
        </v-flex>
        <v-flex class="xs12 sm12 md4 px-3">
          <v-autocomplete
            v-model="itemData.memberRestrictionId"
            label="Member Restriction"
            :placeholder="memberRestrictions[0] ? 'Select Member Restriction' : 'No Applicable Member Restrictions'"
            :items="memberRestrictions"
            auto
            clearable
            :error-messages="getPathErrors(jsonPath, 'memberRestrictionId')"
            :disabled="isFormDisabled"
            item-text="name"
            item-value="id"
            @input="clearPathErrors(jsonPath, 'memberRestrictionId')"
          />
        </v-flex>
      </v-layout>
      <v-flex class="px-3">
        <v-textarea
          v-model.trim="itemData.notes"
          label="Comments"
          :rows="1"
          auto-grow
          :error-messages="getPathErrors(jsonPath, 'notes')"
          :disabled="isFormDisabled"
          @input="clearPathErrors(jsonPath, 'notes')"
        />

        <v-textarea
          v-model.trim="itemData.memberMessage"
          label="Member Message"
          :rows="1"
          auto-grow
          :error-messages="getPathErrors(jsonPath, 'memberMessage')"
          :disabled="isFormDisabled"
          @input="clearPathErrors(jsonPath, 'memberMessage')"
        />

        <days-switch
          v-model="itemData"
          label="Available Days"
          day-prefix="available"
          :disabled="isFormDisabled"
          :get-path-errors="getPathErrors"
          :clear-path-errors="clearPathErrors"
          :required="true"
        />
      </v-flex>

      <v-layout wrap>
        <v-flex class="xs12 sm6 md6 px-3">
          <time-picker
            v-model="itemData.startTime"
            label="Start Time"
            :rules="rules().required()"
            :error-messages="getPathErrors(jsonPath, 'startTime')"
            :disabled="isFormDisabled"
            :min-interval="5"
            required
            @input="clearPathErrors(jsonPath, 'startTime')"
          />
        </v-flex>

        <v-flex class="xs12 sm6 md6 px-3">
          <time-picker
            v-model="itemData.endTime"
            label="End Time"
            :rules="rules().required()"
            :error-messages="getPathErrors(jsonPath, 'endTime')"
            :disabled="isFormDisabled"
            :min-interval="5"
            required
            @input="clearPathErrors(jsonPath, 'endTime')"
          />
        </v-flex>
      </v-layout>
    </expansion-card>

    <v-divider class="my-3" />

    <expansion-card icon="mdi-calendar-repeat">
      <span slot="title">Seasonal</span>
      <v-layout wrap>
        <v-flex class="xs12 sm6 md6 px-3">
          <div style="display: flex; flex-wrap: nowrap;">
            <v-autocomplete
              v-model="itemData.startMonth"
              label="Seasonal Start"
              placeholder="Month"
              :items="seasonalMonths()"
              :rules="seasonalStartMonthRules"
              auto
              :error-messages="getPathErrors(jsonPath, 'startMonth')"
              :disabled="isFormDisabled"
              item-text="text"
              item-value="value"
              @input="clearSeasonalFieldErrors"
            />

            <v-autocomplete
              ref="seasonalStartDay"
              v-model="itemData.startDay"
              class="ml-3"
              style="flex: 0 0 80px;"
              placeholder="Day"
              :items="seasonalDays(itemData.startMonth)"
              :rules="seasonalStartDayRules"
              :error-messages="getPathErrors(jsonPath, 'startDay')"
              auto
              :disabled="isFormDisabled"
              item-text="text"
              item-value="value"
              @input="clearSeasonalFieldErrors"
            />
          </div>
        </v-flex>

        <v-flex class="xs12 sm6 md6 px-3">
          <div style="display: flex; flex-wrap: nowrap;">
            <v-autocomplete
              v-model="itemData.endMonth"
              label="Seasonal End"
              placeholder="Month"
              :items="seasonalMonths()"
              :rules="seasonalEndMonthRules"
              :error-messages="getPathErrors(jsonPath, 'endMonth')"
              auto
              :disabled="isFormDisabled"
              item-text="text"
              item-value="value"
              @input="clearSeasonalFieldErrors"
            />

            <v-autocomplete
              ref="seasonalEndDay"
              v-model="itemData.endDay"
              class="ml-3"
              style="flex: 0 0 80px;"
              placeholder="Day"
              :items="seasonalDays(itemData.endMonth)"
              :rules="seasonalEndDayRules"
              :error-messages="getPathErrors(jsonPath, 'endDay')"
              auto
              :disabled="isFormDisabled"
              item-text="text"
              item-value="value"
              @input="clearSeasonalFieldErrors"
            />
          </div>
        </v-flex>
      </v-layout>
    </expansion-card>

    <v-divider class="my-3" />

    <expansion-card
      icon="mdi-timetable"
      class="pb-1"
    >
      <span slot="title">Seating</span>

      <v-layout wrap>
        <v-flex
          class="px-3"
          :class="{'xs12 sm6 md6': getTimeFormat(itemData.timeFormatId).code === 'ongoing'}"
        >
          <v-select
            v-model="itemData.timeFormatId"
            label="Time Format"
            auto
            :items="timeFormats.filter( format => format.code !== 'single' )"
            :rules="rules().required()"
            required
            :error-messages="getPathErrors(jsonPath, 'timeFormatId')"
            :disabled="isFormDisabled"
            item-text="name"
            item-value="id"
            @input="clearPathErrors(jsonPath, 'timeFormatId'); clearPathErrors(jsonPath, 'seatings');"
          />
        </v-flex>
        <v-flex
          v-if="getTimeFormat(itemData.timeFormatId).code === 'ongoing'"
          class="xs12 sm6 md6 px-3"
        >
          <v-select
            v-model="itemData.timeIncrement"
            label="Time Increment"
            placeholder="Select Increment"
            :items="timeIncrements()"
            :rules="rules().required()"
            required
            auto
            :error-messages="getPathErrors(jsonPath, 'timeIncrement')"
            :disabled="isFormDisabled"
            item-text="text"
            item-value="value"
            @input="clearPathErrors(jsonPath, 'timeIncrement')"
          />
        </v-flex>
      </v-layout>

      <v-expand-transition>
        <detail-editor-table
          v-show="showSeatingsList"
          v-model="itemData.seatings"
          type="Seating"
          :json-path="buildJsonPath('seatings')"
          :get-path-errors="getPathErrors"
          :clear-path-errors="clearPathErrors"
          :move-path-errors="movePathErrors"
          :disabled="isFormDisabled"
          :item-default="{
            id: null,
            mealPeriodId: itemData.id,
            time: '',
            allowMemberMade: true,
            disabled: false
          }"
          :headers="[
            headerEditor({label: 'Time', field: 'time', editor: {type: 'slot'}}),
            headerEditor({label: 'Visible To Members', field: 'allowMemberMade', editor: {type: 'switch'}, thin: true}),
            headerEditor({label: 'Enabled', field: 'disabled', editor: {type: 'switch', invert: true}, thin: true})
          ]"
          @error="addError"
          @add="resetItemFieldErrorLookup"
        >
          <template
            slot="time"
            slot-scope="props"
          >
            <time-picker
              v-model.trim="props.item.time"
              :rules="rules().required()"
              :error-messages="props.errorMessages"
              :disabled="isFormDisabled"
              :min-interval="5"
              required
              @input="props.errorMessages._clear()"
            />
          </template>
        </detail-editor-table>
      </v-expand-transition>
    </expansion-card>

    <v-divider class="my-3" />

    <expansion-card
      icon="mdi-repeat"
      class="pb-1"
    >
      <span slot="title">Turn Rate</span>

      <v-flex class="px-3">
        <!-- TODO make this wrap on smaller screens -->
        <div style="display: flex; flex-wrap: nowrap;">
          <v-text-field
            v-model.trim="itemData.coversPerPeriod"
            label="Covers per"
            :rules="rules().required()"
            type="number"
            :error-messages="getPathErrors(jsonPath, 'coversPerPeriod')"
            :disabled="isFormDisabled"
            required
            @blur="itemData.coversPerPeriod = forceInt(itemData.coversPerPeriod)"
            @input="clearPathErrors(jsonPath, 'coversPerPeriod')"
          />
          <v-text-field
            v-model.trim="itemData.memberCoversPerPeriod"
            class="ml-3"
            label="Member Made Covers per"
            :rules="rules().required()"
            type="number"
            :error-messages="getPathErrors(jsonPath, 'memberCoversPerPeriod')"
            :disabled="isFormDisabled"
            required
            @blur="itemData.memberCoversPerPeriod = forceInt(itemData.memberCoversPerPeriod)"
            @input="clearPathErrors(jsonPath, 'memberCoversPerPeriod')"
          />
          <v-autocomplete
            v-model="itemData.periodId"
            class="ml-3"
            style="flex: 0 0 180px;"
            label="Period"
            placeholder="Select Period"
            :items="periods"
            :rules="rules().required()"
            required
            auto
            :error-messages="getPathErrors(jsonPath, 'periodId')"
            :disabled="isFormDisabled"
            item-text="name"
            item-value="id"
            @input="clearPathErrors(jsonPath, 'periodId')"
          />
        </div>
      </v-flex>

      <v-layout wrap>
        <v-flex class="xs12 sm6 md6 px-3">
          <v-text-field
            v-model.trim="itemData.avgTurnoverInHours"
            label="Average Table Turnover"
            :rules="rules().required()"
            type="number"
            :error-messages="getPathErrors(jsonPath, 'avgTurnoverInHours')"
            hint="e.g. 1.5 = 1hr 30min"
            persistent-hint
            :disabled="isFormDisabled"
            required
            @blur="itemData.coversPerPeriod = forceFloat(itemData.coversPerPeriod)"
            @input="clearPathErrors(jsonPath, 'avgTurnoverInHours')"
          />
        </v-flex>
        <v-flex class="xs12 sm6 md6 px-3">
          <v-layout>
            <v-flex
              class="pr-3"
              style="max-width: 50%"
            >
              <v-text-field
                v-model.trim="itemData.maxPartySize"
                label="Max Party Size"
                auto
                type="number"
                :rules="rules().required()"
                required
                :error-messages="getPathErrors(jsonPath, 'maxPartySize')"
                :disabled="isFormDisabled"
                @blur="itemData.maxPartySize = forceInt(itemData.maxPartySize)"
                @input="clearPathErrors(jsonPath, 'maxPartySize')"
              />
            </v-flex>
            <v-flex>
              <v-text-field
                v-model.trim="itemData.maxPartySizeStaff"
                label="Max Staff Party Size"
                auto
                type="number"
                :rules="rules().required()"
                required
                :error-messages="getPathErrors(jsonPath, 'maxPartySizeStaff')"
                :disabled="isFormDisabled"
                @blur="itemData.maxPartySizeStaff = forceInt(itemData.maxPartySizeStaff)"
                @input="clearPathErrors(jsonPath, 'maxPartySizeStaff')"
              />
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>

      <detail-editor-table
        v-model="itemData.turnRateMultipliers"
        type="Turn Rate Multiplier"
        :json-path="buildJsonPath('turnRateMultipliers')"
        :get-path-errors="getPathErrors"
        :clear-path-errors="clearPathErrors"
        :move-path-errors="movePathErrors"
        :disabled="isFormDisabled"
        :item-default="{
          id: null,
          mealPeriodId: itemData.id,
          threshold: null,
          multiplier: null,
          disabled: false
        }"
        :headers="[
          headerEditor({label: 'Party Size', field: 'threshold', editor: {hint: 'covers', type: 'int', required: true, rules: rules().required()}}),
          headerEditor({label: 'Multiplier', field: 'multiplier', editor: {type: 'number', required: true, rules: rules().required()}}),
          headerEditor({label: 'Average Turnover', field: 'id', editor: {type: 'slot'}})
        ]"
        @error="addError"
      >
        <template
          slot="id"
          slot-scope="props"
        >
          <span v-if="props.item.multiplier && itemData.avgTurnoverInHours">{{ Math.round((itemData.avgTurnoverInHours * props.item.multiplier) * 100) / 100 }} hour(s) per table</span>
        </template>
      </detail-editor-table>
    </expansion-card>

    <v-divider class="my-3" />

    <question-editor
      v-model="itemData.questions"
      :json-path="buildJsonPath('questions')"
      :get-path-errors="getPathErrors"
      :clear-path-errors="clearPathErrors"
      :move-path-errors="movePathErrors"
      :meal-period-id="itemData.id"
      :disabled="isFormDisabled"
      :discrete-covers="itemData.discreteCovers"
      @add="resetItemFieldErrorLookup"
    />

    <v-divider class="my-3" />

    <expansion-card
      icon="mdi-cog"
      class="pb-1"
    >
      <span slot="title">Options</span>
      <v-flex class="mx-2">
        <v-boolean
          v-model="itemData.memberReserve"
          label="Allow Members to Make Reservations"
          :error-messages="getPathErrors(jsonPath, 'memberReserve')"
          :field-errors="getPathErrors(jsonPath, 'memberReserve')"
          :disabled="isFormDisabled"
        />

        <v-textarea
          v-if="!itemData.memberReserve"
          v-model.trim="itemData.noMemberAccessMessage"
          label="No Member Access Message"
          :rows="1"
          auto-grow
          :error-messages="getPathErrors(jsonPath, 'noMemberAccessMessage')"
          :disabled="isFormDisabled"
          @input="clearPathErrors(jsonPath, 'noMemberAccessMessage')"
        />
        <!-- <v-autocomplete
          v-model="itemData.guests"
          label="Guests"
          auto
          :items="guestAllowSelection"
          :error-messages="getPathErrors(jsonPath, 'guests')"
          :disabled="isFormDisabled"
          :persistent-hint="!!(guestAllowSelection.find(g => g.value === itemData.guests) || {}).hint"
          :hint="(guestAllowSelection.find(g => g.value === itemData.guests) || {}).hint"
          @input="clearPathErrors(jsonPath, 'guests')"
        /> -->
        <bound-property-selector
          v-model="itemData.blockedGuestTypes"
          :items="guestTypes"
          label="Restricted Guest Types"
          hint="Members will not be able to use selected guest types"
          persistent-hint
          property-id-field="guestTypeId"
          parent-id-field="mealPeriodId"
          item-id-field="id"
          :parent-id="itemData.id"
          name-field="name"
        />

        <v-boolean
          v-model="itemData.discreteCovers"
          label="Require Full Details for Every Reservee"
          :error-messages="getPathErrors(jsonPath, 'discreteCovers')"
          :field-errors="getPathErrors(jsonPath, 'discreteCovers')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-if="selectedLocationTableConfig.id"
          v-model="itemData.requireTableAssignment"
          label="Require Table Assignment Before Booking"
          :error-messages="getPathErrors(jsonPath, 'requireTableAssignment')"
          :field-errors="getPathErrors(jsonPath, 'requireTableAssignment')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-if="allowAutoAssign && selectedLocationTableConfig.autoAssignable"
          v-model="itemData.autoAssignTables"
          label="Auto Assign Tables"
          :error-messages="getPathErrors(jsonPath, 'autoAssignTables')"
          :field-errors="getPathErrors(jsonPath, 'autoAssignTables')"
          :disabled="isFormDisabled"
        />
        <v-boolean
          v-else-if="allowAutoAssign && selectedLocationTableConfig.id"
          label="Selected Table Config Not Auto-Assignable"
          :value="false"
          :error-messages="getPathErrors(jsonPath, 'autoAssignTables')"
          :field-errors="getPathErrors(jsonPath, 'autoAssignTables')"
          :disabled="true"
        />

        <v-boolean
          v-model="itemData.disabled"
          label="Enabled"
          invert
          :error-messages="getPathErrors(jsonPath, 'disabled')"
          :field-errors="getPathErrors(jsonPath, 'disabled')"
          :disabled="isFormDisabled"
        />
      </v-flex>
    </expansion-card>
    <!-- END OF FORM FIELDS -->
  </item-editor-dialog>
</template>

<script>
import {itemEdit, withChildren} from '@/ft-vue-core'
import QuestionEditor from '../../common/QuestionEditor'
import BoundPropertySelector from '../../common/BoundPropertySelector'

var itemDefault = {
  id: null,
  name: null,
  locationId: null,
  periodId: null,
  timeFormatId: 3, // Default to ongoing
  tableConfigId: null,
  memberRestrictionId: null,
  blockedGuestTypes: [],
  // guests: null,
  startMonth: null,
  startDay: null,
  startTime: null,
  endMonth: null,
  endDay: null,
  endTime: null,
  availableSunday: false,
  availableMonday: false,
  availableTuesday: false,
  availableWednesday: false,
  availableThursday: false,
  availableFriday: false,
  availableSaturday: false,
  maxPartySize: null,
  maxPartySizeStaff: null,
  coversPerPeriod: null,
  memberCoversPerPeriod: null,
  avgTurnoverInHours: null,
  timeIncrement: null,
  notes: null,
  noMemberAccessMessage: null,
  memberMessage: null,
  memberReserve: true,
  discreteCovers: true,
  requireTableAssignment: false,
  autoAssignTables: false,
  disabled: false,
  questions: [],
  seatings: [],
  turnRateMultipliers: [],
  recordLockExpiration: null
}

export default {
  name: 'MealPeriodsItem',
  components: {QuestionEditor, BoundPropertySelector},
  mixins: [itemEdit, withChildren],
  data: function () {
    return {
      type: 'Meal Period',
      types: 'Meal Periods',
      apiRecordCommand: 'diningMealPeriod',
      itemDefault: itemDefault,
      locations: [],
      periods: [],
      timeFormats: [],
      tableConfigs: [],
      memberRestrictions: [],
      // guestAllowSelection: [],
      guestTypes: [],
      cloner: {
        id: null,
        turnRateMultipliers: {
          _cloneMode: Array,
          id: null,
          mealPeriodId: null
        },
        questions: {
          _cloneMode: Array,
          id: null,
          mealPeriodId: null,
          dependentQuestionId: null,
          values: {
            _cloneMode: Array,
            id: null,
            questionId: null
          }
        },
        seatings: {
          _cloneMode: Array,
          id: null,
          mealPeriodId: null
        },
        blockedGuestTypes: {
          _cloneMode: Array,
          mealPeriodId: null
        }
      }
    }
  },
  computed: {
    allowAutoAssign () {
      return this.$ftCS.DINING_ENABLE_TABLE_AUTO_ASSIGN
    },
    selectedLocationId () {
      return this.itemData.locationId
    },
    selectedLocationTableConfigs () {
      const locationId = this.selectedLocationId
      return this.tableConfigs.filter(tc => tc.locationId === locationId && (!tc.disabled && tc.allowMealPeriod || tc.id === this.itemData.tableConfigId))
    },
    selectedLocationTableConfig () {
      return this.selectedLocationTableConfigs.find(tc => tc.id === this.itemData.tableConfigId) || {}
    },
    seasonalStartMonthRules () {
      if (!this.itemData.endMonth && !this.itemData.startMonth) return []
      return [
        (v) => {this.$refs.seasonalStartDay.validate(); return !!this.itemData.startMonth || 'Seasonal end requires start'}
      ]
    },
    seasonalEndMonthRules () {
      if (!this.itemData.endMonth && !this.itemData.startMonth) return []
      return [
        (v) => {this.$refs.seasonalEndDay.validate(); return !!this.itemData.endMonth || 'Seasonal start requires end'}
      ]
    },
    seasonalStartDayRules () {
      return [
        (v) => {
          const days = this.daysInMonth(this.itemData.startMonth)
          if (!this.itemData.endMonth && !this.itemData.startMonth) {
            return true
          }
          return this.itemData.startDay && this.itemData.startDay <= days || 'Required'
        }
      ]
    },
    seasonalEndDayRules () {
      return [
        (v) => {
          const days = this.daysInMonth(this.itemData.endMonth)
          if (!this.itemData.endMonth && !this.itemData.startMonth) {
            return true
          }
          return this.itemData.endDay >= 1 && this.itemData.endDay <= days || 'Required'
        }
      ]
    },
    timeFormatMap () {
      return this.timeFormats.reduce((acc, t) => {
        acc[t.id] = t
        return acc
      }, {})
    },
    showSeatingsList () {
      return (this.timeFormatMap[this.itemData.timeFormatId] || {}).code !== 'ongoing'
    }
  },
  watch: {
    selectedLocationId (newVal, oldVal) {
      if (!this.isLoading && newVal !== oldVal && newVal) {
        const location = this.locations.find(location => location.id === newVal)
        this.itemData.coversPerPeriod = location.defaultCoversPerQtrHour
        this.itemData.memberCoversPerPeriod = location.defaultMemberCoversPerQtrHour
        this.itemData.periodId = 1 // Quarter Hour
        this.itemData.maxPartySize = location.maxPartySize
        this.itemData.maxPartySizeStaff = location.maxPartySizeStaff
      }
    }
  },
  methods: {
    itemResourceCommands () {
      return [
        {command: 'diningLocation', load: this.locations, loading: this.isLoading},
        {command: 'diningTableConfiguration', load: this.tableConfigs, loading: this.isLoading, params: {children: true, include_fields: ['autoAssignable']}},
        {command: 'diningPeriod', load: this.periods, loading: this.isLoading},
        {command: 'diningTimeFormat', load: this.timeFormats, loading: this.isLoading},
        {command: 'memberRestriction', load: this.memberRestrictions, loading: this.isLoading},
        {command: 'guestType', load: this.guestTypes, loading: this.isLoading, params: {filter_methods: {filterActivityId: [9999]}}},
        // {
        //   command: 'apiFieldOption',
        //   load: r => {
        //     this.guestAllowSelection = r
        //     this.itemDefault.guests = r[0].value
        //     if (this.itemData.guests === null) {
        //       this.itemData.guests = r[0].value
        //     }
        //   },
        //   loading: this.isLoading,
        //   params: {filter_methods: {forField: ['diningMealPeriod', 'guests']}}
        // }
      ]
    },
    withChildrenItemCommandFilter (command) {
      this.addCommandParamArrayVal(command, 'include_fields', 'seatings')
      return command
    },
    daysInMonth (month) {
      // Hardcoded 2016 to allow leap day (feb 29th)
      return new Date(2016, month, 0).getDate()
    },
    seasonalDays (month) {
      const result = []
      if (month >= 1 && month <= 12) {
        for (let i = 1, l = this.daysInMonth(month); i <= l; i += 1) {
          result.push({text: i, value: i})
        }
      }
      return result
    },
    seasonalMonths () {
      return [
        {text: 'None', value: null},
        {text: 'January', value: 1},
        {text: 'February', value: 2},
        {text: 'March', value: 3},
        {text: 'April', value: 4},
        {text: 'May', value: 5},
        {text: 'June', value: 6},
        {text: 'July', value: 7},
        {text: 'August', value: 8},
        {text: 'September', value: 9},
        {text: 'October', value: 10},
        {text: 'November', value: 11},
        {text: 'December', value: 12}
      ]
    },
    timeIncrements () {
      const result = []
      for (let i = 5; i <= 60; i += 5) {
        result.push({text: i + ' Minutes', value: i})
      }
      return result
    },
    clearSeasonalFieldErrors () {
      this.clearPathErrors(this.jsonPath, 'startMonth')
      this.clearPathErrors(this.jsonPath, 'startDay')
      this.clearPathErrors(this.jsonPath, 'endMonth')
      this.clearPathErrors(this.jsonPath, 'endDay')
    },
    resetLocalValues () {
      this.startTimeModal = false
      this.endTimeModal = false
    },
    getTimeFormat (id) {
      for (let timeFormat of this.timeFormats) {
        if (timeFormat.id === id) return timeFormat
      }
      return {}
    }
  }
}
</script>
